import React, { useState, useEffect, useCallback } from "react";
import { Database, Search, Loader2, Trash2, X, AlertTriangle, Cog, Brain, BookOpen, Library, BookMarked, RotateCcw, Shield, Link2, Save, Check } from "lucide-react";
import { BACKEND_URL } from "@/config/api";
import AdminNav from "../components/AdminNav";
import AdminTopMenuBar from "../components/AdminTopMenuBar";
import AdminMobileBottomNav from "../components/AdminMobileBottomNav";
import UniversalFooter from "../components/UniversalFooter";

const getAuthToken = () =>
  localStorage.getItem("accessToken") || localStorage.getItem("token");

const AdminHousekeepingPage = () => {
  // Stale ingestion jobs state
  const [staleJobs, setStaleJobs] = useState(null);
  const [totalJobs, setTotalJobs] = useState(0);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState(null);
  const [jobsSelected, setJobsSelected] = useState(new Set());
  const [showJobsModal, setShowJobsModal] = useState(false);
  const [jobsDeleting, setJobsDeleting] = useState(false);
  const [jobsDeleteResult, setJobsDeleteResult] = useState(null);

  // Ephemeral intelligence state
  const [eiRecords, setEiRecords] = useState(null);
  const [totalEi, setTotalEi] = useState(0);
  const [eiLoading, setEiLoading] = useState(false);
  const [eiError, setEiError] = useState(null);
  const [eiSelected, setEiSelected] = useState(new Set());
  const [showEiModal, setShowEiModal] = useState(false);
  const [eiDeleting, setEiDeleting] = useState(false);
  const [eiDeleteResult, setEiDeleteResult] = useState(null);

  // Manuscripts state
  const [orphanedMs, setOrphanedMs] = useState(null);
  const [totalMs, setTotalMs] = useState(0);
  const [msLoading, setMsLoading] = useState(false);
  const [msError, setMsError] = useState(null);
  const [msSelected, setMsSelected] = useState(new Set());
  const [showMsModal, setShowMsModal] = useState(false);
  const [msDeleting, setMsDeleting] = useState(false);
  const [msDeleteResult, setMsDeleteResult] = useState(null);

  // Publisher titles state
  const [orphanedPt, setOrphanedPt] = useState(null);
  const [totalPt, setTotalPt] = useState(0);
  const [ptLoading, setPtLoading] = useState(false);
  const [ptError, setPtError] = useState(null);
  const [ptSelected, setPtSelected] = useState(new Set());
  const [showPtModal, setShowPtModal] = useState(false);
  const [ptDeleting, setPtDeleting] = useState(false);
  const [ptDeleteResult, setPtDeleteResult] = useState(null);

  // Reader library state
  const [orphanedRl, setOrphanedRl] = useState(null);
  const [totalRl, setTotalRl] = useState(0);
  const [rlLoading, setRlLoading] = useState(false);
  const [rlError, setRlError] = useState(null);
  const [rlSelected, setRlSelected] = useState(new Set());
  const [showRlModal, setShowRlModal] = useState(false);
  const [rlDeleting, setRlDeleting] = useState(false);
  const [rlDeleteResult, setRlDeleteResult] = useState(null);

  // Backup & Restore shared state
  const [backupStatus, setBackupStatus] = useState({});
  const [backupInProgress, setBackupInProgress] = useState(null); // section name or null
  const [restoreSection, setRestoreSection] = useState(null); // section to restore
  const [restoring, setRestoring] = useState(false);

  // Manuscript Publisher Relink state
  const [relinkManuscripts, setRelinkManuscripts] = useState(null);
  const [relinkLoading, setRelinkLoading] = useState(false);
  const [relinkError, setRelinkError] = useState(null);
  const [relinkEdits, setRelinkEdits] = useState({}); // {manuscript_id: edited_publisher_id}
  const [relinkSaving, setRelinkSaving] = useState({}); // {manuscript_id: true}
  const [relinkSaved, setRelinkSaved] = useState({}); // {manuscript_id: true}

  // Managed Author Relink state
  const [managedAuthors, setManagedAuthors] = useState(null);
  const [maLoading, setMaLoading] = useState(false);
  const [maError, setMaError] = useState(null);
  const [maEdits, setMaEdits] = useState({});
  const [maSaving, setMaSaving] = useState({});
  const [maSaved, setMaSaved] = useState({});

  // Publisher Owner Relink state
  const [pubOwners, setPubOwners] = useState(null);
  const [poLoading, setPoLoading] = useState(false);
  const [poError, setPoError] = useState(null);
  const [poEdits, setPoEdits] = useState({});
  const [poSaving, setPoSaving] = useState({});
  const [poSaved, setPoSaved] = useState({});

  const SECTION_COLLECTIONS = {
    "stale-ingestion-jobs": ["ingest_jobs"],
    "orphaned-ephemeral-intelligence": ["ephemeral_intelligence", "ephemeral_intelligence_jobs", "ephemeral_intelligence_segments"],
    "orphaned-manuscripts": ["manuscripts"],
    "orphaned-publisher-titles": ["publisher_titles"],
    "orphaned-reader-library": ["reader_library"],
  };

  const fetchBackupStatus = useCallback(async (section) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/backup-status?section=${section}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setBackupStatus((prev) => ({ ...prev, [section]: data }));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    Object.keys(SECTION_COLLECTIONS).forEach((s) => fetchBackupStatus(s));
  }, [fetchBackupStatus]);

  const doBackup = async (section, recordIds) => {
    setBackupInProgress(section);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/backup`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ section, collections: SECTION_COLLECTIONS[section], record_ids: recordIds }),
      });
      if (!res.ok) throw new Error(`Backup failed (${res.status})`);
      const data = await res.json();
      setBackupStatus((prev) => ({
        ...prev,
        [section]: { exists: true, timestamp: data.timestamp, record_counts: data.record_counts },
      }));
      return true;
    } catch (e) {
      return e.message;
    } finally {
      setBackupInProgress(null);
    }
  };

  const doRestore = async (section) => {
    setRestoring(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/restore`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ section }),
      });
      if (!res.ok) throw new Error(`Restore failed (${res.status})`);
      await res.json();
      setRestoreSection(null);
      fetchBackupStatus(section);
      return true;
    } catch (e) {
      return e.message;
    } finally {
      setRestoring(false);
    }
  };

  const RestoreButton = ({ section }) => {
    const status = backupStatus[section];
    if (!status?.exists) return null;
    const totalRecords = Object.values(status.record_counts || {}).reduce((a, b) => a + b, 0);
    const colls = Object.keys(status.record_counts || {}).join(", ");
    return (
      <div className="mt-4 p-3 rounded-lg bg-slate-800/40 border border-slate-700/50 flex items-center justify-between gap-3">
        <div className="text-xs text-slate-400">
          <Shield className="w-3.5 h-3.5 inline mr-1 text-emerald-400" />
          Last backup: {new Date(status.timestamp).toLocaleString()} — {totalRecords} records across {colls}
        </div>
        <button
          onClick={() => setRestoreSection(section)}
          data-testid={`restore-${section}-btn`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-900/40 border border-blue-800/50 text-blue-300 text-xs hover:bg-blue-900/60 hover:border-blue-700 transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Restore Last Backup
        </button>
      </div>
    );
  };

  const loadRelinkManuscripts = async () => {
    setRelinkLoading(true);
    setRelinkError(null);
    setRelinkEdits({});
    setRelinkSaved({});
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/all-manuscripts`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setRelinkManuscripts(data.manuscripts);
    } catch (e) {
      setRelinkError(e.message);
    } finally {
      setRelinkLoading(false);
    }
  };

  const handleRelinkSave = async (manuscriptId) => {
    const newPubId = relinkEdits[manuscriptId];
    if (!newPubId) return;
    setRelinkSaving((p) => ({ ...p, [manuscriptId]: true }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/relink-manuscript-publisher`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ manuscript_id: manuscriptId, new_publisher_id: newPubId.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed (${res.status})`);
      }
      // Update local state
      setRelinkManuscripts((prev) =>
        prev.map((m) => m.id === manuscriptId ? { ...m, publisher_id: newPubId.trim() } : m)
      );
      setRelinkEdits((p) => { const n = { ...p }; delete n[manuscriptId]; return n; });
      setRelinkSaved((p) => ({ ...p, [manuscriptId]: true }));
      setTimeout(() => setRelinkSaved((p) => { const n = { ...p }; delete n[manuscriptId]; return n; }), 2000);
    } catch (e) {
      alert(`Relink failed: ${e.message}`);
    } finally {
      setRelinkSaving((p) => { const n = { ...p }; delete n[manuscriptId]; return n; });
    }
  };

  const loadPubOwners = async () => {
    setPoLoading(true);
    setPoError(null);
    setPoEdits({});
    setPoSaved({});
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/all-publishers`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setPubOwners(data.publishers);
    } catch (e) {
      setPoError(e.message);
    } finally {
      setPoLoading(false);
    }
  };

  const handlePoSave = async (publisherId) => {
    const newOwnerId = poEdits[publisherId];
    if (!newOwnerId) return;
    setPoSaving((p) => ({ ...p, [publisherId]: true }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/relink-publisher-owner`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ publisher_id: publisherId, new_owner_user_id: newOwnerId.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed (${res.status})`);
      }
      setPubOwners((prev) =>
        prev.map((p) => p.id === publisherId ? { ...p, owner_user_id: newOwnerId.trim() } : p)
      );
      setPoEdits((p) => { const n = { ...p }; delete n[publisherId]; return n; });
      setPoSaved((p) => ({ ...p, [publisherId]: true }));
      setTimeout(() => setPoSaved((p) => { const n = { ...p }; delete n[publisherId]; return n; }), 2000);
    } catch (e) {
      alert(`Relink failed: ${e.message}`);
    } finally {
      setPoSaving((p) => { const n = { ...p }; delete n[publisherId]; return n; });
    }
  };

  const loadManagedAuthors = async () => {
    setMaLoading(true);
    setMaError(null);
    setMaEdits({});
    setMaSaved({});
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/all-managed-authors`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setManagedAuthors(data.managed_authors);
    } catch (e) {
      setMaError(e.message);
    } finally {
      setMaLoading(false);
    }
  };

  const handleMaSave = async (managedAuthorId) => {
    const newPubId = maEdits[managedAuthorId];
    if (!newPubId) return;
    setMaSaving((p) => ({ ...p, [managedAuthorId]: true }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/relink-managed-author-publisher`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ managed_author_id: managedAuthorId, new_publisher_id: newPubId.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed (${res.status})`);
      }
      setManagedAuthors((prev) =>
        prev.map((ma) => ma.id === managedAuthorId ? { ...ma, publisher_id: newPubId.trim() } : ma)
      );
      setMaEdits((p) => { const n = { ...p }; delete n[managedAuthorId]; return n; });
      setMaSaved((p) => ({ ...p, [managedAuthorId]: true }));
      setTimeout(() => setMaSaved((p) => { const n = { ...p }; delete n[managedAuthorId]; return n; }), 2000);
    } catch (e) {
      alert(`Relink failed: ${e.message}`);
    } finally {
      setMaSaving((p) => { const n = { ...p }; delete n[managedAuthorId]; return n; });
    }
  };

  const findStaleJobs = async () => {
    setJobsLoading(true);
    setJobsError(null);
    setJobsSelected(new Set());
    setJobsDeleteResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/stale-ingestion-jobs`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setStaleJobs(data.stale_jobs);
      setTotalJobs(data.total_jobs);
    } catch (e) {
      setJobsError(e.message);
    } finally {
      setJobsLoading(false);
    }
  };

  const toggleJobSelect = (id) => {
    setJobsSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllJobs = () => {
    if (!staleJobs) return;
    if (jobsSelected.size === staleJobs.length) {
      setJobsSelected(new Set());
    } else {
      setJobsSelected(new Set(staleJobs.map((j) => j.job_id)));
    }
  };

  const selectedJobs = staleJobs
    ? staleJobs.filter((j) => jobsSelected.has(j.job_id))
    : [];

  const handleDeleteJobs = async () => {
    setJobsDeleting(true);
    try {
      const backupResult = await doBackup("stale-ingestion-jobs", [...jobsSelected]);
      if (backupResult !== true) {
        setJobsError(`Backup failed. Deletion has been cancelled. (${backupResult})`);
        setShowJobsModal(false);
        return;
      }
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/stale-ingestion-jobs`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ job_ids: [...jobsSelected] }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setJobsDeleteResult(data);
      setShowJobsModal(false);
      if (data.deleted?.length) {
        const deletedSet = new Set(data.deleted);
        setStaleJobs((prev) => prev.filter((j) => !deletedSet.has(j.job_id)));
        setJobsSelected(new Set());
      }
      fetchBackupStatus("stale-ingestion-jobs");
    } catch (e) {
      setJobsError(e.message);
      setShowJobsModal(false);
    } finally {
      setJobsDeleting(false);
    }
  };

  const findOrphanedEI = async () => {
    setEiLoading(true);
    setEiError(null);
    setEiSelected(new Set());
    setEiDeleteResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/orphaned-ephemeral-intelligence`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setEiRecords(data.orphaned_records);
      setTotalEi(data.total_records);
    } catch (e) {
      setEiError(e.message);
    } finally {
      setEiLoading(false);
    }
  };

  // EI records use record_id as the key for selection
  const toggleEiSelect = (rid) => {
    setEiSelected((prev) => {
      const next = new Set(prev);
      next.has(rid) ? next.delete(rid) : next.add(rid);
      return next;
    });
  };

  const toggleAllEi = () => {
    if (!eiRecords) return;
    if (eiSelected.size === eiRecords.length) {
      setEiSelected(new Set());
    } else {
      setEiSelected(new Set(eiRecords.map((r) => r.record_id)));
    }
  };

  const selectedEi = eiRecords
    ? eiRecords.filter((r) => eiSelected.has(r.record_id))
    : [];

  const handleDeleteEi = async () => {
    setEiDeleting(true);
    try {
      const backupResult = await doBackup("orphaned-ephemeral-intelligence", [...eiSelected]);
      if (backupResult !== true) {
        setEiError(`Backup failed. Deletion has been cancelled. (${backupResult})`);
        setShowEiModal(false);
        return;
      }
      const payload = selectedEi.map((r) => ({
        record_id: r.record_id,
        collection: r.collection,
        manuscript_id: r.manuscript_id,
      }));
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/orphaned-ephemeral-intelligence`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ records: payload }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setEiDeleteResult(data);
      setShowEiModal(false);
      if (data.deleted?.length) {
        const deletedSet = new Set(data.deleted);
        setEiRecords((prev) => prev.filter((r) => !deletedSet.has(r.record_id)));
        setEiSelected(new Set());
      }
      fetchBackupStatus("orphaned-ephemeral-intelligence");
    } catch (e) {
      setEiError(e.message);
      setShowEiModal(false);
    } finally {
      setEiDeleting(false);
    }
  };

  const findOrphanedMs = async () => {
    setMsLoading(true);
    setMsError(null);
    setMsSelected(new Set());
    setMsDeleteResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/orphaned-manuscripts`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setOrphanedMs(data.orphaned_manuscripts);
      setTotalMs(data.total_manuscripts);
    } catch (e) {
      setMsError(e.message);
    } finally {
      setMsLoading(false);
    }
  };

  const toggleMsSelect = (oid) => {
    setMsSelected((prev) => {
      const next = new Set(prev);
      next.has(oid) ? next.delete(oid) : next.add(oid);
      return next;
    });
  };

  const toggleAllMs = () => {
    if (!orphanedMs) return;
    setMsSelected(msSelected.size === orphanedMs.length ? new Set() : new Set(orphanedMs.map((m) => m.oid)));
  };

  const selectedMs = orphanedMs ? orphanedMs.filter((m) => msSelected.has(m.oid)) : [];

  const handleDeleteMs = async () => {
    setMsDeleting(true);
    try {
      const backupResult = await doBackup("orphaned-manuscripts", [...msSelected]);
      if (backupResult !== true) {
        setMsError(`Backup failed. Deletion has been cancelled. (${backupResult})`);
        setShowMsModal(false);
        return;
      }
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/orphaned-manuscripts`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ record_oids: [...msSelected] }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setMsDeleteResult(data);
      setShowMsModal(false);
      if (data.deleted?.length) {
        const deletedSet = new Set(data.deleted);
        setOrphanedMs((prev) => prev.filter((m) => !deletedSet.has(m.oid)));
        setMsSelected(new Set());
      }
      fetchBackupStatus("orphaned-manuscripts");
    } catch (e) {
      setMsError(e.message);
      setShowMsModal(false);
    } finally {
      setMsDeleting(false);
    }
  };

  const findOrphanedPt = async () => {
    setPtLoading(true);
    setPtError(null);
    setPtSelected(new Set());
    setPtDeleteResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/orphaned-publisher-titles`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setOrphanedPt(data.orphaned_titles);
      setTotalPt(data.total_titles);
    } catch (e) {
      setPtError(e.message);
    } finally {
      setPtLoading(false);
    }
  };

  const togglePtSelect = (oid) => {
    setPtSelected((prev) => {
      const next = new Set(prev);
      next.has(oid) ? next.delete(oid) : next.add(oid);
      return next;
    });
  };

  const toggleAllPt = () => {
    if (!orphanedPt) return;
    setPtSelected(ptSelected.size === orphanedPt.length ? new Set() : new Set(orphanedPt.map((t) => t.oid)));
  };

  const selectedPt = orphanedPt ? orphanedPt.filter((t) => ptSelected.has(t.oid)) : [];

  const handleDeletePt = async () => {
    setPtDeleting(true);
    try {
      const backupResult = await doBackup("orphaned-publisher-titles", [...ptSelected]);
      if (backupResult !== true) {
        setPtError(`Backup failed. Deletion has been cancelled. (${backupResult})`);
        setShowPtModal(false);
        return;
      }
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/orphaned-publisher-titles`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ title_oids: [...ptSelected] }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setPtDeleteResult(data);
      setShowPtModal(false);
      if (data.deleted?.length) {
        const deletedSet = new Set(data.deleted);
        setOrphanedPt((prev) => prev.filter((t) => !deletedSet.has(t.oid)));
        setPtSelected(new Set());
      }
      fetchBackupStatus("orphaned-publisher-titles");
    } catch (e) {
      setPtError(e.message);
      setShowPtModal(false);
    } finally {
      setPtDeleting(false);
    }
  };

  // Reader Library handlers
  const findOrphanedRl = async () => {
    setRlLoading(true);
    setRlError(null);
    setRlSelected(new Set());
    setRlDeleteResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/orphaned-reader-library`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setOrphanedRl(data.orphaned_entries);
      setTotalRl(data.total_entries);
    } catch (e) {
      setRlError(e.message);
    } finally {
      setRlLoading(false);
    }
  };

  const toggleRlSelect = (oid) => {
    setRlSelected((prev) => {
      const next = new Set(prev);
      next.has(oid) ? next.delete(oid) : next.add(oid);
      return next;
    });
  };

  const toggleAllRl = () => {
    if (!orphanedRl) return;
    setRlSelected(rlSelected.size === orphanedRl.length ? new Set() : new Set(orphanedRl.map((e) => e.oid)));
  };

  const selectedRl = orphanedRl ? orphanedRl.filter((e) => rlSelected.has(e.oid)) : [];

  const handleDeleteRl = async () => {
    setRlDeleting(true);
    try {
      const backupResult = await doBackup("orphaned-reader-library", [...rlSelected]);
      if (backupResult !== true) {
        setRlError(`Backup failed. Deletion has been cancelled. (${backupResult})`);
        setShowRlModal(false);
        return;
      }
      const res = await fetch(`${BACKEND_URL}/api/admin/housekeeping/orphaned-reader-library`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ record_oids: [...rlSelected] }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setRlDeleteResult(data);
      setShowRlModal(false);
      if (data.deleted?.length) {
        const deletedSet = new Set(data.deleted);
        setOrphanedRl((prev) => prev.filter((e) => !deletedSet.has(e.oid)));
        setRlSelected(new Set());
      }
      fetchBackupStatus("orphaned-reader-library");
    } catch (e) {
      setRlError(e.message);
      setShowRlModal(false);
    } finally {
      setRlDeleting(false);
    }
  };

  // SECTION_FIND_FNS must be defined AFTER all the find* functions to avoid TDZ errors
  const SECTION_FIND_FNS = {
    "stale-ingestion-jobs": findStaleJobs,
    "orphaned-ephemeral-intelligence": findOrphanedEI,
    "orphaned-manuscripts": findOrphanedMs,
    "orphaned-publisher-titles": findOrphanedPt,
    "orphaned-reader-library": findOrphanedRl,
  };

  const handleRestore = async () => {
    const section = restoreSection;
    const result = await doRestore(section);
    if (result === true && SECTION_FIND_FNS[section]) {
      SECTION_FIND_FNS[section]();
    }
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="admin-housekeeping-page">
      <AdminNav />
      <AdminTopMenuBar />

      <main className="mx-auto max-w-7xl px-4 py-8 pb-24 md:pb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-amber-900/50">
            <Database className="w-6 h-6 text-amber-400" />
          </div>
          <h1 className="text-3xl font-bold text-white" data-testid="housekeeping-heading">
            Database Housekeeping
          </h1>
        </div>
        <p className="text-slate-400 mb-10 ml-[3.25rem]" data-testid="housekeeping-subheading">
          Use the tools below to identify candidate records for cleanup.
        </p>

        {/* Manuscript Publisher Relink Section */}
        <section className="mb-10" data-testid="relink-section">
          <div className="flex items-center gap-2 mb-4">
            <Link2 className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-semibold text-white" data-testid="relink-heading">
              Manuscript Publisher Relink
            </h2>
          </div>

          <button
            onClick={loadRelinkManuscripts}
            disabled={relinkLoading}
            data-testid="load-all-manuscripts-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {relinkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {relinkLoading ? "Loading..." : "Load All Manuscripts"}
          </button>

          {relinkError && (
            <div className="mt-4 p-3 rounded-lg bg-red-900/30 border border-red-800/50 text-red-300 text-sm" data-testid="relink-error">
              {relinkError}
            </div>
          )}

          {relinkManuscripts && (
            <div className="mt-4 overflow-x-auto rounded-lg border border-slate-700" data-testid="relink-table-container">
              <div className="px-4 py-2 bg-slate-800/60 text-xs text-slate-400 border-b border-slate-700">
                {relinkManuscripts.length} manuscripts loaded
              </div>
              <table className="w-full text-sm" data-testid="relink-table">
                <thead className="bg-slate-800/80 text-slate-400 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2 text-left">Book Title</th>
                    <th className="px-4 py-2 text-left">Author</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left min-w-[340px]">Publisher ID</th>
                    <th className="px-4 py-2 text-center w-[100px]">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {relinkManuscripts.map((ms) => {
                    const currentVal = relinkEdits[ms.id] !== undefined ? relinkEdits[ms.id] : (ms.publisher_id || "");
                    const isEdited = relinkEdits[ms.id] !== undefined && relinkEdits[ms.id] !== (ms.publisher_id || "");
                    const isSaving = relinkSaving[ms.id];
                    const justSaved = relinkSaved[ms.id];
                    return (
                      <tr key={ms.id} className="hover:bg-slate-800/40" data-testid={`relink-row-${ms.id}`}>
                        <td className="px-4 py-2 font-mono text-xs text-slate-400 max-w-[180px] truncate" title={ms.id}>{ms.id}</td>
                        <td className="px-4 py-2 text-white truncate max-w-[200px]" title={ms.title}>{ms.title || "Untitled"}</td>
                        <td className="px-4 py-2 text-slate-300 truncate max-w-[150px]">{ms.author || "—"}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${ms.status === "active" ? "bg-emerald-900/40 text-emerald-300" : "bg-slate-700 text-slate-300"}`}>
                            {ms.status || "unknown"}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={currentVal}
                            onChange={(e) => setRelinkEdits((p) => ({ ...p, [ms.id]: e.target.value }))}
                            data-testid={`relink-input-${ms.id}`}
                            className={`w-full px-2 py-1 rounded text-xs font-mono bg-slate-900 border text-slate-200 outline-none transition-colors ${isEdited ? "border-amber-500" : "border-slate-700"} focus:border-blue-500`}
                            placeholder="No publisher linked"
                          />
                        </td>
                        <td className="px-4 py-2 text-center">
                          {justSaved ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><Check className="w-3.5 h-3.5" /> Saved</span>
                          ) : isEdited ? (
                            <button
                              onClick={() => handleRelinkSave(ms.id)}
                              disabled={isSaving}
                              data-testid={`relink-save-${ms.id}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-amber-700/60 border border-amber-600/50 text-amber-200 hover:bg-amber-700/80 transition-colors disabled:opacity-50"
                            >
                              {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                              Save
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {/* Publisher Owner Relink Table */}
          <div className="mt-6">
            <button
              onClick={loadPubOwners}
              disabled={poLoading}
              data-testid="load-publishers-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {poLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {poLoading ? "Loading..." : "Load All Publishers"}
            </button>

            {poError && (
              <div className="mt-4 p-3 rounded-lg bg-red-900/30 border border-red-800/50 text-red-300 text-sm" data-testid="po-error">
                {poError}
              </div>
            )}

            {pubOwners && (
              <div className="mt-4 overflow-x-auto rounded-lg border border-slate-700" data-testid="po-table-container">
                <div className="px-4 py-2 bg-slate-800/60 text-xs text-slate-400 border-b border-slate-700">
                  {pubOwners.length} publishers loaded
                </div>
                <table className="w-full text-sm" data-testid="po-table">
                  <thead className="bg-slate-800/80 text-slate-400 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-2 text-left">Publisher ID</th>
                      <th className="px-4 py-2 text-left">Publisher Name</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left min-w-[340px]">Owner User ID</th>
                      <th className="px-4 py-2 text-center w-[100px]">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {pubOwners.map((pub) => {
                      const currentVal = poEdits[pub.id] !== undefined ? poEdits[pub.id] : (pub.owner_user_id || "");
                      const isEdited = poEdits[pub.id] !== undefined && poEdits[pub.id] !== (pub.owner_user_id || "");
                      const isSaving = poSaving[pub.id];
                      const justSaved = poSaved[pub.id];
                      return (
                        <tr key={pub.id} className="hover:bg-slate-800/40" data-testid={`po-row-${pub.id}`}>
                          <td className="px-4 py-2 font-mono text-xs text-slate-400 max-w-[180px] truncate" title={pub.id}>{pub.id}</td>
                          <td className="px-4 py-2 text-white">{pub.dba_name || pub.legal_business_name || "—"}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${pub.status === "active" ? "bg-emerald-900/40 text-emerald-300" : "bg-slate-700 text-slate-300"}`}>
                              {pub.status || "unknown"}
                            </span>
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={currentVal}
                              onChange={(e) => setPoEdits((p) => ({ ...p, [pub.id]: e.target.value }))}
                              data-testid={`po-input-${pub.id}`}
                              className={`w-full px-2 py-1 rounded text-xs font-mono bg-slate-900 border text-slate-200 outline-none transition-colors ${isEdited ? "border-amber-500" : "border-slate-700"} focus:border-blue-500`}
                              placeholder="No owner linked"
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            {justSaved ? (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><Check className="w-3.5 h-3.5" /> Saved</span>
                            ) : isEdited ? (
                              <button
                                onClick={() => handlePoSave(pub.id)}
                                disabled={isSaving}
                                data-testid={`po-save-${pub.id}`}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-amber-700/60 border border-amber-600/50 text-amber-200 hover:bg-amber-700/80 transition-colors disabled:opacity-50"
                              >
                                {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                Save
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Managed Author Relink Table */}
          <div className="mt-6">
            <button
              onClick={loadManagedAuthors}
              disabled={maLoading}
              data-testid="load-managed-authors-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {maLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {maLoading ? "Loading..." : "Load All Managed Authors"}
            </button>

            {maError && (
              <div className="mt-4 p-3 rounded-lg bg-red-900/30 border border-red-800/50 text-red-300 text-sm" data-testid="ma-error">
                {maError}
              </div>
            )}

            {managedAuthors && (
              <div className="mt-4 overflow-x-auto rounded-lg border border-slate-700" data-testid="ma-table-container">
                <div className="px-4 py-2 bg-slate-800/60 text-xs text-slate-400 border-b border-slate-700">
                  {managedAuthors.length} managed authors loaded
                </div>
                <table className="w-full text-sm" data-testid="ma-table">
                  <thead className="bg-slate-800/80 text-slate-400 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-2 text-left">Managed Author ID</th>
                      <th className="px-4 py-2 text-left">Pen Name</th>
                      <th className="px-4 py-2 text-left min-w-[340px]">Publisher ID</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-center w-[100px]">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {managedAuthors.map((ma) => {
                      const currentVal = maEdits[ma.id] !== undefined ? maEdits[ma.id] : (ma.publisher_id || "");
                      const isEdited = maEdits[ma.id] !== undefined && maEdits[ma.id] !== (ma.publisher_id || "");
                      const isSaving = maSaving[ma.id];
                      const justSaved = maSaved[ma.id];
                      return (
                        <tr key={ma.id} className="hover:bg-slate-800/40" data-testid={`ma-row-${ma.id}`}>
                          <td className="px-4 py-2 font-mono text-xs text-slate-400 max-w-[180px] truncate" title={ma.id}>{ma.id}</td>
                          <td className="px-4 py-2 text-white">{ma.pen_name || "—"}</td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={currentVal}
                              onChange={(e) => setMaEdits((p) => ({ ...p, [ma.id]: e.target.value }))}
                              data-testid={`ma-input-${ma.id}`}
                              className={`w-full px-2 py-1 rounded text-xs font-mono bg-slate-900 border text-slate-200 outline-none transition-colors ${isEdited ? "border-amber-500" : "border-slate-700"} focus:border-blue-500`}
                              placeholder="No publisher linked"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${ma.status === "active" ? "bg-emerald-900/40 text-emerald-300" : "bg-slate-700 text-slate-300"}`}>
                              {ma.status || "unknown"}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-center">
                            {justSaved ? (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><Check className="w-3.5 h-3.5" /> Saved</span>
                            ) : isEdited ? (
                              <button
                                onClick={() => handleMaSave(ma.id)}
                                disabled={isSaving}
                                data-testid={`ma-save-${ma.id}`}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-amber-700/60 border border-amber-600/50 text-amber-200 hover:bg-amber-700/80 transition-colors disabled:opacity-50"
                              >
                                {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                Save
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* Ingestion Jobs Section */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Cog className="w-5 h-5 text-slate-300" />
            <h2 className="text-lg font-semibold text-white" data-testid="ingestion-jobs-heading">
              Ingestion Jobs
            </h2>
          </div>

          <button
            onClick={findStaleJobs}
            disabled={jobsLoading}
            data-testid="find-stale-jobs-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {jobsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {jobsLoading ? "Scanning..." : "Find Stale Ingestion Jobs"}
          </button>

          <div className="mt-4" data-testid="stale-jobs-results">
            {jobsError && (
              <p className="text-red-400 text-sm" data-testid="stale-jobs-error">
                Error: {jobsError}
              </p>
            )}

            {jobsDeleteResult && (
              <div className="mb-3 p-3 rounded-lg bg-slate-800/60 border border-slate-700 text-sm" data-testid="jobs-delete-result">
                {jobsDeleteResult.deleted?.length > 0 && (
                  <p className="text-emerald-400">
                    Deleted {jobsDeleteResult.deleted.length} job{jobsDeleteResult.deleted.length !== 1 ? "s" : ""}.
                  </p>
                )}
                {jobsDeleteResult.skipped?.length > 0 && (
                  <p className="text-amber-400 mt-1">
                    Skipped {jobsDeleteResult.skipped.length}: {jobsDeleteResult.skipped.map((s) => s.reason).join(", ")}
                  </p>
                )}
              </div>
            )}

            {staleJobs !== null && !jobsError && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-slate-400">
                    Found {staleJobs.length} stale job{staleJobs.length !== 1 ? "s" : ""} out of {totalJobs} total
                  </p>
                  {staleJobs.length > 0 && (
                    <button
                      onClick={toggleAllJobs}
                      className="text-xs text-amber-400 hover:text-amber-300 transition-colors"
                      data-testid="toggle-all-jobs-btn"
                    >
                      {jobsSelected.size === staleJobs.length ? "Deselect All" : "Select All"}
                    </button>
                  )}
                </div>

                {staleJobs.length === 0 ? (
                  <p className="text-slate-500 text-sm" data-testid="no-stale-jobs">
                    No stale ingestion jobs found.
                  </p>
                ) : (
                  <>
                    <div className="border border-slate-800 rounded-lg overflow-hidden">
                      <table className="w-full text-sm" data-testid="stale-jobs-table">
                        <thead className="bg-slate-900/60">
                          <tr className="text-left text-slate-400">
                            <th className="px-4 py-2.5 w-10">
                              <input
                                type="checkbox"
                                checked={jobsSelected.size === staleJobs.length && staleJobs.length > 0}
                                onChange={toggleAllJobs}
                                className="rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/30"
                                data-testid="select-all-jobs-checkbox"
                              />
                            </th>
                            <th className="px-4 py-2.5 font-medium">Job ID</th>
                            <th className="px-4 py-2.5 font-medium">Book Title</th>
                            <th className="px-4 py-2.5 font-medium">Status</th>
                            <th className="px-4 py-2.5 font-medium">Gate</th>
                            <th className="px-4 py-2.5 font-medium">Last Updated</th>
                            <th className="px-4 py-2.5 font-medium">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {staleJobs.map((j) => (
                            <tr
                              key={j.job_id}
                              className="border-t border-slate-800 text-slate-300 hover:bg-slate-800/40"
                            >
                              <td className="px-4 py-2.5">
                                <input
                                  type="checkbox"
                                  checked={jobsSelected.has(j.job_id)}
                                  onChange={() => toggleJobSelect(j.job_id)}
                                  className="rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/30"
                                  data-testid={`select-job-${j.job_id.slice(0, 8)}`}
                                />
                              </td>
                              <td className="px-4 py-2.5 font-mono text-xs">{j.job_id.slice(0, 8)}</td>
                              <td className="px-4 py-2.5 text-xs max-w-[200px] truncate" title={j.manuscript_id}>{j.manuscript_title || j.manuscript_id?.slice(0, 8) || "—"}</td>
                              <td className="px-4 py-2.5">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  j.status.toLowerCase() === "failed"
                                    ? "bg-red-900/40 text-red-300"
                                    : "bg-amber-900/40 text-amber-300"
                                }`}>
                                  {j.status}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-xs">{j.current_gate || "—"}</td>
                              <td className="px-4 py-2.5 whitespace-nowrap text-xs">
                                {j.updated_at ? new Date(j.updated_at).toLocaleString() : "—"}
                              </td>
                              <td className="px-4 py-2.5 text-xs text-slate-400">
                                {j.reasons?.join("; ") || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <button
                      onClick={() => setShowJobsModal(true)}
                      disabled={jobsSelected.size === 0}
                      data-testid="delete-selected-jobs-btn"
                      className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-red-900/40 border border-red-800/60 text-red-300 hover:bg-red-900/60 hover:border-red-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Selected ({jobsSelected.size})
                    </button>
                  </>
                )}
              </>
            )}
          </div>
          <RestoreButton section="stale-ingestion-jobs" />
        </section>

        {/* Ephemeral Intelligence Section */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Brain className="w-5 h-5 text-slate-300" />
            <h2 className="text-lg font-semibold text-white" data-testid="ei-heading">
              Ephemeral Intelligence
            </h2>
          </div>

          <button
            onClick={findOrphanedEI}
            disabled={eiLoading}
            data-testid="find-orphaned-ei-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {eiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {eiLoading ? "Scanning..." : "Find Orphaned Ephemeral Intelligence Records"}
          </button>

          <div className="mt-4" data-testid="ei-results">
            {eiError && (
              <p className="text-red-400 text-sm" data-testid="ei-error">
                Error: {eiError}
              </p>
            )}

            {eiDeleteResult && (
              <div className="mb-3 p-3 rounded-lg bg-slate-800/60 border border-slate-700 text-sm" data-testid="ei-delete-result">
                {eiDeleteResult.deleted?.length > 0 && (
                  <p className="text-emerald-400">
                    Deleted {eiDeleteResult.deleted.length} record{eiDeleteResult.deleted.length !== 1 ? "s" : ""}.
                  </p>
                )}
                {eiDeleteResult.skipped?.length > 0 && (
                  <p className="text-amber-400 mt-1">
                    Skipped {eiDeleteResult.skipped.length}: {eiDeleteResult.skipped.map((s) => s.reason).join(", ")}
                  </p>
                )}
              </div>
            )}

            {eiRecords !== null && !eiError && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-slate-400">
                    Found {eiRecords.length} orphaned record{eiRecords.length !== 1 ? "s" : ""} out of {totalEi} total
                  </p>
                  {eiRecords.length > 0 && (
                    <button
                      onClick={toggleAllEi}
                      className="text-xs text-amber-400 hover:text-amber-300 transition-colors"
                      data-testid="toggle-all-ei-btn"
                    >
                      {eiSelected.size === eiRecords.length ? "Deselect All" : "Select All"}
                    </button>
                  )}
                </div>

                {eiRecords.length === 0 ? (
                  <p className="text-slate-500 text-sm" data-testid="no-orphaned-ei">
                    No orphaned ephemeral intelligence records found.
                  </p>
                ) : (
                  <>
                    <div className="border border-slate-800 rounded-lg overflow-hidden">
                      <table className="w-full text-sm" data-testid="ei-table">
                        <thead className="bg-slate-900/60">
                          <tr className="text-left text-slate-400">
                            <th className="px-4 py-2.5 w-10">
                              <input
                                type="checkbox"
                                checked={eiSelected.size === eiRecords.length && eiRecords.length > 0}
                                onChange={toggleAllEi}
                                className="rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/30"
                                data-testid="select-all-ei-checkbox"
                              />
                            </th>
                            <th className="px-4 py-2.5 font-medium">Record ID</th>
                            <th className="px-4 py-2.5 font-medium">Book Title</th>
                            <th className="px-4 py-2.5 font-medium">Collection</th>
                            <th className="px-4 py-2.5 font-medium">Status</th>
                            <th className="px-4 py-2.5 font-medium">Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {eiRecords.map((r, idx) => (
                            <tr
                              key={r.record_id || idx}
                              className="border-t border-slate-800 text-slate-300 hover:bg-slate-800/40"
                            >
                              <td className="px-4 py-2.5">
                                <input
                                  type="checkbox"
                                  checked={eiSelected.has(r.record_id)}
                                  onChange={() => toggleEiSelect(r.record_id)}
                                  className="rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/30"
                                  data-testid={`select-ei-${(r.record_id || "").slice(0, 8)}`}
                                />
                              </td>
                              <td className="px-4 py-2.5 font-mono text-xs">{(r.record_id || "—").slice(0, 12)}</td>
                              <td className="px-4 py-2.5 text-xs max-w-[200px] truncate" title={r.manuscript_id}>{r.manuscript_title || r.manuscript_id?.slice(0, 8) || "—"}</td>
                              <td className="px-4 py-2.5 text-xs">
                                <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300">
                                  {r.collection.replace("ephemeral_intelligence", "ei").replace("_", ".")}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-xs">{r.status || "—"}</td>
                              <td className="px-4 py-2.5 whitespace-nowrap text-xs">
                                {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <button
                      onClick={() => setShowEiModal(true)}
                      disabled={eiSelected.size === 0}
                      data-testid="delete-selected-ei-btn"
                      className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-red-900/40 border border-red-800/60 text-red-300 hover:bg-red-900/60 hover:border-red-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Selected ({eiSelected.size})
                    </button>
                  </>
                )}
              </>
            )}
          </div>
          <RestoreButton section="orphaned-ephemeral-intelligence" />
        </section>

        {/* Manuscripts Section */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-5 h-5 text-slate-300" />
            <h2 className="text-lg font-semibold text-white" data-testid="manuscripts-heading">
              Manuscripts
            </h2>
          </div>

          <button
            onClick={findOrphanedMs}
            disabled={msLoading}
            data-testid="find-orphaned-ms-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {msLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {msLoading ? "Scanning..." : "Find Orphaned Manuscripts"}
          </button>

          <div className="mt-4" data-testid="ms-results">
            {msError && (
              <p className="text-red-400 text-sm" data-testid="ms-error">
                Error: {msError}
              </p>
            )}

            {msDeleteResult && (
              <div className="mb-3 p-3 rounded-lg bg-slate-800/60 border border-slate-700 text-sm" data-testid="ms-delete-result">
                {msDeleteResult.deleted?.length > 0 && (
                  <p className="text-emerald-400">
                    Deleted {msDeleteResult.deleted.length} manuscript{msDeleteResult.deleted.length !== 1 ? "s" : ""}.
                  </p>
                )}
                {msDeleteResult.skipped?.length > 0 && (
                  <p className="text-amber-400 mt-1">
                    Skipped {msDeleteResult.skipped.length}: {msDeleteResult.skipped.map((s) => s.reason).join(", ")}
                  </p>
                )}
              </div>
            )}

            {orphanedMs !== null && !msError && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-slate-400">
                    Found {orphanedMs.length} orphaned manuscript{orphanedMs.length !== 1 ? "s" : ""} out of {totalMs} total
                  </p>
                  {orphanedMs.length > 0 && (
                    <button
                      onClick={toggleAllMs}
                      className="text-xs text-amber-400 hover:text-amber-300 transition-colors"
                      data-testid="toggle-all-ms-btn"
                    >
                      {msSelected.size === orphanedMs.length ? "Deselect All" : "Select All"}
                    </button>
                  )}
                </div>

                {orphanedMs.length === 0 ? (
                  <p className="text-slate-500 text-sm" data-testid="no-orphaned-ms">
                    No orphaned manuscripts found.
                  </p>
                ) : (
                  <>
                    <div className="border border-slate-800 rounded-lg overflow-hidden">
                      <table className="w-full text-sm" data-testid="ms-table">
                        <thead className="bg-slate-900/60">
                          <tr className="text-left text-slate-400">
                            <th className="px-4 py-2.5 w-10">
                              <input
                                type="checkbox"
                                checked={msSelected.size === orphanedMs.length && orphanedMs.length > 0}
                                onChange={toggleAllMs}
                                className="rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/30"
                                data-testid="select-all-ms-checkbox"
                              />
                            </th>
                            <th className="px-4 py-2.5 font-medium">Book Title</th>
                            <th className="px-4 py-2.5 font-medium">Author</th>
                            <th className="px-4 py-2.5 font-medium">Missing</th>
                            <th className="px-4 py-2.5 font-medium">Status</th>
                            <th className="px-4 py-2.5 font-medium">Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orphanedMs.map((m) => (
                            <tr
                              key={m.oid}
                              className="border-t border-slate-800 text-slate-300 hover:bg-slate-800/40"
                            >
                              <td className="px-4 py-2.5">
                                <input
                                  type="checkbox"
                                  checked={msSelected.has(m.oid)}
                                  onChange={() => toggleMsSelect(m.oid)}
                                  className="rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/30"
                                  data-testid={`select-ms-${m.oid.slice(0, 8)}`}
                                />
                              </td>
                              <td className="px-4 py-2.5 text-sm max-w-[200px] truncate" title={m.id}>{m.title || "Untitled"}</td>
                              <td className="px-4 py-2.5 text-xs">{m.author || "—"}</td>
                              <td className="px-4 py-2.5 text-xs">
                                <div className="flex flex-wrap gap-1">
                                  {(m.missing_fields || []).map((f) => (
                                    <span key={f} className="px-1.5 py-0.5 rounded bg-red-900/40 text-red-300 text-xs">
                                      {f}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td className="px-4 py-2.5 text-xs">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  m.status === "active" ? "bg-emerald-900/40 text-emerald-300" :
                                  m.status === "deactivated" ? "bg-slate-700 text-slate-400" :
                                  "bg-amber-900/40 text-amber-300"
                                }`}>
                                  {m.status || "—"}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 whitespace-nowrap text-xs">
                                {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <button
                      onClick={() => setShowMsModal(true)}
                      disabled={msSelected.size === 0}
                      data-testid="delete-selected-ms-btn"
                      className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-red-900/40 border border-red-800/60 text-red-300 hover:bg-red-900/60 hover:border-red-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Selected ({msSelected.size})
                    </button>
                  </>
                )}
              </>
            )}
          </div>
          <RestoreButton section="orphaned-manuscripts" />
        </section>

        {/* Publisher Titles Section */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Library className="w-5 h-5 text-slate-300" />
            <h2 className="text-lg font-semibold text-white" data-testid="pt-heading">
              Publisher Titles
            </h2>
          </div>

          <button
            onClick={findOrphanedPt}
            disabled={ptLoading}
            data-testid="find-orphaned-pt-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {ptLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {ptLoading ? "Scanning..." : "Find Orphaned Publisher Title Records"}
          </button>

          <div className="mt-4" data-testid="pt-results">
            {ptError && (
              <p className="text-red-400 text-sm" data-testid="pt-error">
                Error: {ptError}
              </p>
            )}

            {ptDeleteResult && (
              <div className="mb-3 p-3 rounded-lg bg-slate-800/60 border border-slate-700 text-sm" data-testid="pt-delete-result">
                {ptDeleteResult.deleted?.length > 0 && (
                  <p className="text-emerald-400">
                    Deleted {ptDeleteResult.deleted.length} title{ptDeleteResult.deleted.length !== 1 ? "s" : ""}.
                  </p>
                )}
                {ptDeleteResult.skipped?.length > 0 && (
                  <p className="text-amber-400 mt-1">
                    Skipped {ptDeleteResult.skipped.length}: {ptDeleteResult.skipped.map((s) => s.reason).join(", ")}
                  </p>
                )}
              </div>
            )}

            {orphanedPt !== null && !ptError && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-slate-400">
                    Found {orphanedPt.length} orphaned title{orphanedPt.length !== 1 ? "s" : ""} out of {totalPt} total
                  </p>
                  {orphanedPt.length > 0 && (
                    <button
                      onClick={toggleAllPt}
                      className="text-xs text-amber-400 hover:text-amber-300 transition-colors"
                      data-testid="toggle-all-pt-btn"
                    >
                      {ptSelected.size === orphanedPt.length ? "Deselect All" : "Select All"}
                    </button>
                  )}
                </div>

                {orphanedPt.length === 0 ? (
                  <p className="text-slate-500 text-sm" data-testid="no-orphaned-pt">
                    No orphaned publisher title records found.
                  </p>
                ) : (
                  <>
                    <div className="border border-slate-800 rounded-lg overflow-hidden">
                      <table className="w-full text-sm" data-testid="pt-table">
                        <thead className="bg-slate-900/60">
                          <tr className="text-left text-slate-400">
                            <th className="px-4 py-2.5 w-10">
                              <input
                                type="checkbox"
                                checked={ptSelected.size === orphanedPt.length && orphanedPt.length > 0}
                                onChange={toggleAllPt}
                                className="rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/30"
                                data-testid="select-all-pt-checkbox"
                              />
                            </th>
                            <th className="px-4 py-2.5 font-medium">Title ID</th>
                            <th className="px-4 py-2.5 font-medium">Book Title</th>
                            <th className="px-4 py-2.5 font-medium">Publisher ID</th>
                            <th className="px-4 py-2.5 font-medium">Status</th>
                            <th className="px-4 py-2.5 font-medium">Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orphanedPt.map((t) => (
                            <tr
                              key={t.oid}
                              className="border-t border-slate-800 text-slate-300 hover:bg-slate-800/40"
                            >
                              <td className="px-4 py-2.5">
                                <input
                                  type="checkbox"
                                  checked={ptSelected.has(t.oid)}
                                  onChange={() => togglePtSelect(t.oid)}
                                  className="rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/30"
                                  data-testid={`select-pt-${t.oid.slice(0, 8)}`}
                                />
                              </td>
                              <td className="px-4 py-2.5 font-mono text-xs">{t.id.slice(0, 8)}</td>
                              <td className="px-4 py-2.5 text-sm max-w-[200px] truncate" title={t.manuscript_id}>{t.title || "—"}</td>
                              <td className="px-4 py-2.5 font-mono text-xs">{t.publisher_id ? t.publisher_id.slice(0, 8) : "—"}</td>
                              <td className="px-4 py-2.5 text-xs">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  t.status === "active" ? "bg-emerald-900/40 text-emerald-300" :
                                  "bg-slate-700 text-slate-400"
                                }`}>
                                  {t.status || "—"}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 whitespace-nowrap text-xs">
                                {t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <button
                      onClick={() => setShowPtModal(true)}
                      disabled={ptSelected.size === 0}
                      data-testid="delete-selected-pt-btn"
                      className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-red-900/40 border border-red-800/60 text-red-300 hover:bg-red-900/60 hover:border-red-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Selected ({ptSelected.size})
                    </button>
                  </>
                )}
              </>
            )}
          </div>
          <RestoreButton section="orphaned-publisher-titles" />
        </section>

        {/* Reader Library Section */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <BookMarked className="w-5 h-5 text-slate-300" />
            <h2 className="text-lg font-semibold text-white" data-testid="rl-heading">
              Reader Library
            </h2>
          </div>

          <button
            onClick={findOrphanedRl}
            disabled={rlLoading}
            data-testid="find-orphaned-rl-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {rlLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {rlLoading ? "Scanning..." : "Find Orphaned Reader Library Entries"}
          </button>

          <div className="mt-4" data-testid="rl-results">
            {rlError && (
              <p className="text-red-400 text-sm" data-testid="rl-error">
                Error: {rlError}
              </p>
            )}

            {rlDeleteResult && (
              <div className="mb-3 p-3 rounded-lg bg-slate-800/60 border border-slate-700 text-sm" data-testid="rl-delete-result">
                {rlDeleteResult.deleted?.length > 0 && (
                  <p className="text-emerald-400">
                    Deleted {rlDeleteResult.deleted.length} entr{rlDeleteResult.deleted.length !== 1 ? "ies" : "y"}.
                  </p>
                )}
                {rlDeleteResult.skipped?.length > 0 && (
                  <p className="text-amber-400 mt-1">
                    Skipped {rlDeleteResult.skipped.length}: {rlDeleteResult.skipped.map((s) => s.reason).join(", ")}
                  </p>
                )}
              </div>
            )}

            {orphanedRl !== null && !rlError && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-slate-400">
                    Found {orphanedRl.length} orphaned entr{orphanedRl.length !== 1 ? "ies" : "y"} out of {totalRl} total
                  </p>
                  {orphanedRl.length > 0 && (
                    <button
                      onClick={toggleAllRl}
                      className="text-xs text-amber-400 hover:text-amber-300 transition-colors"
                      data-testid="toggle-all-rl-btn"
                    >
                      {rlSelected.size === orphanedRl.length ? "Deselect All" : "Select All"}
                    </button>
                  )}
                </div>

                {orphanedRl.length === 0 ? (
                  <p className="text-slate-500 text-sm" data-testid="no-orphaned-rl">
                    No orphaned reader library entries found.
                  </p>
                ) : (
                  <>
                    <div className="border border-slate-800 rounded-lg overflow-hidden">
                      <table className="w-full text-sm" data-testid="rl-table">
                        <thead className="bg-slate-900/60">
                          <tr className="text-left text-slate-400">
                            <th className="px-4 py-2.5 w-10">
                              <input
                                type="checkbox"
                                checked={rlSelected.size === orphanedRl.length && orphanedRl.length > 0}
                                onChange={toggleAllRl}
                                className="rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/30"
                                data-testid="select-all-rl-checkbox"
                              />
                            </th>
                            <th className="px-4 py-2.5 font-medium">User ID</th>
                            <th className="px-4 py-2.5 font-medium">Book Title</th>
                            <th className="px-4 py-2.5 font-medium">Listen (s)</th>
                            <th className="px-4 py-2.5 font-medium">Completion %</th>
                            <th className="px-4 py-2.5 font-medium">Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orphanedRl.map((e) => (
                            <tr
                              key={e.oid}
                              className="border-t border-slate-800 text-slate-300 hover:bg-slate-800/40"
                            >
                              <td className="px-4 py-2.5">
                                <input
                                  type="checkbox"
                                  checked={rlSelected.has(e.oid)}
                                  onChange={() => toggleRlSelect(e.oid)}
                                  className="rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/30"
                                  data-testid={`select-rl-${e.oid.slice(0, 8)}`}
                                />
                              </td>
                              <td className="px-4 py-2.5 font-mono text-xs">{e.user_id ? e.user_id.slice(0, 8) : "—"}</td>
                              <td className="px-4 py-2.5 text-xs max-w-[200px] truncate" title={e.manuscript_id}>{e.manuscript_title || e.manuscript_id?.slice(0, 8) || "—"}</td>
                              <td className="px-4 py-2.5 text-xs">{Math.round(e.total_listen_seconds || 0)}</td>
                              <td className="px-4 py-2.5 text-xs">{(e.completion_percent || 0).toFixed(1)}%</td>
                              <td className="px-4 py-2.5 whitespace-nowrap text-xs">
                                {e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <button
                      onClick={() => setShowRlModal(true)}
                      disabled={rlSelected.size === 0}
                      data-testid="delete-selected-rl-btn"
                      className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-red-900/40 border border-red-800/60 text-red-300 hover:bg-red-900/60 hover:border-red-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Selected ({rlSelected.size})
                    </button>
                  </>
                )}
              </>
            )}
          </div>
          <RestoreButton section="orphaned-reader-library" />
        </section>
      </main>
      {/* Jobs Confirmation Modal */}
      {showJobsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          data-testid="delete-jobs-confirm-modal"
        >
          <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-red-900/50">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white" data-testid="jobs-modal-heading">
                Confirm Deletion
              </h3>
              <button
                onClick={() => setShowJobsModal(false)}
                className="ml-auto p-1 text-slate-400 hover:text-white transition-colors"
                data-testid="jobs-modal-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-red-300 mb-4" data-testid="jobs-modal-warning">
              You are about to permanently delete the following ingestion jobs. This cannot be undone.
            </p>

            <div className="max-h-48 overflow-y-auto mb-6 border border-slate-800 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/60">
                  <tr className="text-left text-slate-500 text-xs">
                    <th className="px-4 py-1.5">Job ID</th>
                    <th className="px-4 py-1.5">Book Title</th>
                    <th className="px-4 py-1.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedJobs.map((j) => (
                    <tr key={j.job_id} className="border-t border-slate-800 text-slate-300 text-xs">
                      <td className="px-4 py-2 font-mono">{j.job_id.slice(0, 8)}</td>
                      <td className="px-4 py-2 max-w-[200px] truncate" title={j.manuscript_id}>{j.manuscript_title || j.manuscript_id?.slice(0, 8) || "—"}</td>
                      <td className="px-4 py-2">{j.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowJobsModal(false)}
                disabled={jobsDeleting}
                data-testid="jobs-modal-cancel-btn"
                className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteJobs}
                disabled={jobsDeleting || backupInProgress === "stale-ingestion-jobs"}
                data-testid="jobs-modal-confirm-delete-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-700 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {backupInProgress === "stale-ingestion-jobs" ? <Loader2 className="w-4 h-4 animate-spin" /> : jobsDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {backupInProgress === "stale-ingestion-jobs" ? "Creating backup..." : jobsDeleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EI Confirmation Modal */}
      {showEiModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          data-testid="delete-ei-confirm-modal"
        >
          <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-red-900/50">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white" data-testid="ei-modal-heading">
                Confirm Deletion
              </h3>
              <button
                onClick={() => setShowEiModal(false)}
                className="ml-auto p-1 text-slate-400 hover:text-white transition-colors"
                data-testid="ei-modal-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-red-300 mb-4" data-testid="ei-modal-warning">
              You are about to permanently delete the following ephemeral intelligence records. This cannot be undone.
            </p>

            <div className="max-h-48 overflow-y-auto mb-6 border border-slate-800 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/60">
                  <tr className="text-left text-slate-500 text-xs">
                    <th className="px-4 py-1.5">Record ID</th>
                    <th className="px-4 py-1.5">Book Title</th>
                    <th className="px-4 py-1.5">Collection</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedEi.map((r, idx) => (
                    <tr key={r.record_id || idx} className="border-t border-slate-800 text-slate-300 text-xs">
                      <td className="px-4 py-2 font-mono">{(r.record_id || "—").slice(0, 12)}</td>
                      <td className="px-4 py-2 max-w-[200px] truncate" title={r.manuscript_id}>{r.manuscript_title || r.manuscript_id?.slice(0, 8) || "—"}</td>
                      <td className="px-4 py-2">{r.collection.replace("ephemeral_intelligence", "ei").replace("_", ".")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowEiModal(false)}
                disabled={eiDeleting}
                data-testid="ei-modal-cancel-btn"
                className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteEi}
                disabled={eiDeleting || backupInProgress === "orphaned-ephemeral-intelligence"}
                data-testid="ei-modal-confirm-delete-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-700 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {backupInProgress === "orphaned-ephemeral-intelligence" ? <Loader2 className="w-4 h-4 animate-spin" /> : eiDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {backupInProgress === "orphaned-ephemeral-intelligence" ? "Creating backup..." : eiDeleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manuscripts Confirmation Modal */}
      {showMsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          data-testid="delete-ms-confirm-modal"
        >
          <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-red-900/50">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white" data-testid="ms-modal-heading">
                Confirm Deletion
              </h3>
              <button
                onClick={() => setShowMsModal(false)}
                className="ml-auto p-1 text-slate-400 hover:text-white transition-colors"
                data-testid="ms-modal-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-red-300 mb-4" data-testid="ms-modal-warning">
              You are about to permanently delete the following manuscripts. This cannot be undone.
            </p>

            <div className="max-h-48 overflow-y-auto mb-6 border border-slate-800 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/60">
                  <tr className="text-left text-slate-500 text-xs">
                    <th className="px-4 py-1.5">Book Title</th>
                    <th className="px-4 py-1.5">Missing</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedMs.map((m) => (
                    <tr key={m.oid} className="border-t border-slate-800 text-slate-300 text-xs">
                      <td className="px-4 py-2 max-w-[200px] truncate" title={m.id}>{m.title || "Untitled"}</td>
                      <td className="px-4 py-2">{(m.missing_fields || []).join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowMsModal(false)}
                disabled={msDeleting}
                data-testid="ms-modal-cancel-btn"
                className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteMs}
                disabled={msDeleting || backupInProgress === "orphaned-manuscripts"}
                data-testid="ms-modal-confirm-delete-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-700 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {backupInProgress === "orphaned-manuscripts" ? <Loader2 className="w-4 h-4 animate-spin" /> : msDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {backupInProgress === "orphaned-manuscripts" ? "Creating backup..." : msDeleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publisher Titles Confirmation Modal */}
      {showPtModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          data-testid="delete-pt-confirm-modal"
        >
          <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-red-900/50">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white" data-testid="pt-modal-heading">
                Confirm Deletion
              </h3>
              <button
                onClick={() => setShowPtModal(false)}
                className="ml-auto p-1 text-slate-400 hover:text-white transition-colors"
                data-testid="pt-modal-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-red-300 mb-4" data-testid="pt-modal-warning">
              You are about to permanently delete the following publisher title records. This cannot be undone.
            </p>

            <div className="max-h-48 overflow-y-auto mb-6 border border-slate-800 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/60">
                  <tr className="text-left text-slate-500 text-xs">
                    <th className="px-4 py-1.5">Title ID</th>
                    <th className="px-4 py-1.5">Book Title</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPt.map((t) => (
                    <tr key={t.oid} className="border-t border-slate-800 text-slate-300 text-xs">
                      <td className="px-4 py-2 font-mono">{t.id.slice(0, 8)}</td>
                      <td className="px-4 py-2 max-w-[200px] truncate" title={t.manuscript_id}>{t.title || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowPtModal(false)}
                disabled={ptDeleting}
                data-testid="pt-modal-cancel-btn"
                className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePt}
                disabled={ptDeleting || backupInProgress === "orphaned-publisher-titles"}
                data-testid="pt-modal-confirm-delete-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-700 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {backupInProgress === "orphaned-publisher-titles" ? <Loader2 className="w-4 h-4 animate-spin" /> : ptDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {backupInProgress === "orphaned-publisher-titles" ? "Creating backup..." : ptDeleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reader Library Confirmation Modal */}
      {showRlModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          data-testid="delete-rl-confirm-modal"
        >
          <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-red-900/50">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white" data-testid="rl-modal-heading">
                Confirm Deletion
              </h3>
              <button
                onClick={() => setShowRlModal(false)}
                className="ml-auto p-1 text-slate-400 hover:text-white transition-colors"
                data-testid="rl-modal-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-red-300 mb-4" data-testid="rl-modal-warning">
              You are about to permanently delete the following reader library entries. This cannot be undone.
            </p>

            <div className="max-h-48 overflow-y-auto mb-6 border border-slate-800 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/60">
                  <tr className="text-left text-slate-500 text-xs">
                    <th className="px-4 py-1.5">User ID</th>
                    <th className="px-4 py-1.5">Book Title</th>
                    <th className="px-4 py-1.5">Completion</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedRl.map((e) => (
                    <tr key={e.oid} className="border-t border-slate-800 text-slate-300 text-xs">
                      <td className="px-4 py-2 font-mono">{e.user_id ? e.user_id.slice(0, 8) : "—"}</td>
                      <td className="px-4 py-2 max-w-[200px] truncate" title={e.manuscript_id}>{e.manuscript_title || e.manuscript_id?.slice(0, 8) || "—"}</td>
                      <td className="px-4 py-2">{(e.completion_percent || 0).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRlModal(false)}
                disabled={rlDeleting}
                data-testid="rl-modal-cancel-btn"
                className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteRl}
                disabled={rlDeleting || backupInProgress === "orphaned-reader-library"}
                data-testid="rl-modal-confirm-delete-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-700 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {backupInProgress === "orphaned-reader-library" ? <Loader2 className="w-4 h-4 animate-spin" /> : rlDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {backupInProgress === "orphaned-reader-library" ? "Creating backup..." : rlDeleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {restoreSection && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          data-testid="restore-confirm-modal"
        >
          <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-blue-900/50">
                <RotateCcw className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-white" data-testid="restore-modal-heading">
                Confirm Restore
              </h3>
              <button
                onClick={() => setRestoreSection(null)}
                className="ml-auto p-1 text-slate-400 hover:text-white transition-colors"
                data-testid="restore-modal-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-blue-300 mb-4" data-testid="restore-modal-warning">
              This will restore{" "}
              <span className="font-mono text-white">
                {Object.keys(backupStatus[restoreSection]?.record_counts || {}).join(", ")}
              </span>{" "}
              to their state at{" "}
              <span className="text-white">
                {backupStatus[restoreSection]?.timestamp
                  ? new Date(backupStatus[restoreSection].timestamp).toLocaleString()
                  : "unknown"}
              </span>
              . Current data will be overwritten. This cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRestoreSection(null)}
                disabled={restoring}
                data-testid="restore-modal-cancel-btn"
                className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRestore}
                disabled={restoring}
                data-testid="restore-modal-confirm-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-700 text-white hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                {restoring ? "Restoring..." : "Confirm Restore"}
              </button>
            </div>
          </div>
        </div>
      )}

      <AdminMobileBottomNav />
      <UniversalFooter />
    </div>
  );
};

export default AdminHousekeepingPage;
